// benefit-section.tsx
import { graphql } from 'gatsby'
import React, { useContext } from 'react'
import { Col, Container, Row } from 'react-bootstrap'

import Cta from '@/components/blocks-library/cta/cta'
import Image from '@/components/image/image'
import { ColorContext } from '@/contexts/color.context'

import * as styles from './styles.module.scss'

/**
 * The BenefitSectionBlock component.
 *
 * This component receives its props from the `Queries.DatoCmsBenefitSection` type which is generated automatically by Gatsby.
 *
 * Here is a brief description of the main props:
 *
 * - `title`: The title of the section.
 * - `subtitle`: The subtitle of the section.
 * - `isDark`: If true, a dark theme is used for the section.
 * - `items`: The items to display in the section.
 * - `moreInfo`: Additional information to display in the section.
 * - `image`: The image to display in the section.
 *
 * ```tsx
 * <BenefitSectionBlock
 *     title="Title"
 *     subtitle="Subtitle"
 *     isDark={true}
 *     items={itemsArray}
 *     moreInfo={moreInfoArray}
 *     image={imageObject}
 * />
 * ```
 *
 * @category Block Library
 */
const BenefitSectionBlock: React.FC<Queries.BenefitSectionFragment> = ({
  title,
  subtitle,
  isDark,
  items,
  moreInfo,
  image
}) => {
  const colour = useContext(ColorContext)

  let benefitStyle = styles.benefitSection
  if (colour && colour.isLight === true) {
    benefitStyle = styles.benefitSectionLight
  }
  if (colour && colour.isLight === false) {
    benefitStyle = styles.benefitSectionDark
  }
  if (isDark !== null && isDark === true) {
    benefitStyle = styles.benefitSectionDark
  }

  const cta = moreInfo && moreInfo.length > 0 ? moreInfo[0] : null
  const bgColor = {
    backgroundColor:
      isDark !== null && isDark === false && colour?.colour?.hex
        ? colour?.colour?.hex
        : ''
  }
  return (
    <div className={benefitStyle} style={bgColor}>
      <Container className="pt-4 pb-5">
        <Row className="pt-3 pb-3">
          <Col lg={6} md={12} className="pe-0 pe-lg-5">
            <h2>{title}</h2>
            <h3>{subtitle}</h3>
            <Image
              image={image?.gatsbyImageData}
              className="d-none d-lg-block"
            />
          </Col>
          <Col lg={6} md={12}>
            <Row className={styles.columns}>
              {items?.map(
                (item) =>
                  item !== null && (
                    <Col
                      lg={6}
                      md={6}
                      key={item.id}
                      className="px-3 pe-lg-3 ps-lg-4"
                    >
                      <div className={`${styles.iconImage} mb-4 mt-2`}>
                        <Image
                          image={item.icon}
                          alt="icon"
                          className="justify-self-center"
                        />
                      </div>
                      <h4>{item.title}</h4>
                      <p>{item.subtitle}</p>
                    </Col>
                  )
              )}
            </Row>
          </Col>
        </Row>
        {cta !== null && (
          <Row className="mt-5 pb-3">
            <Col className={`text-center btn-section ${styles.btnSection}`}>
              <Cta {...cta} />
            </Col>
          </Row>
        )}
      </Container>
    </div>
  )
}

export default BenefitSectionBlock

/**
 * This is the GraphQL query to fetch the data for the benefit section.
 */
export const query = graphql`
  fragment BenefitSection on DatoCmsBenefitSection {
    __typename
    id
    navigationLabel
    title
    subtitle
    image {
      gatsbyImageData(width: 650)
      url
    }
    items {
      id
      title
      subtitle
      icon {
        gatsbyImageData(height: 80)
        url
      }
    }
    moreInfo {
      ... on DatoCmsCta {
        __typename
        ...Cta
      }
    }
    isDark
  }
`
